import { useDebounceValue } from 'noddi-util';
import { useEffect, useState } from 'react';
import type { FilterControl, TimeRangeControl } from '../../../../../types/filterControl';
import { NoddiTimeField } from '../../../../Inputs/NoddiTimeField';

export const TimeRangeFilter = ({
  control,
  renderFilterTitle
}: {
  control: TimeRangeControl;
  renderFilterTitle: (control: FilterControl) => React.ReactNode;
}) => {
  const [localFrom, setLocalFrom] = useState<string>(control.value?.from ?? '');
  const [localTo, setLocalTo] = useState<string>(control.value?.to ?? '');

  const debouncedFrom = useDebounceValue({ value: localFrom, delay: 500 });
  const debouncedTo = useDebounceValue({ value: localTo, delay: 500 });

  useEffect(() => {
    const fromValue = debouncedFrom || undefined;
    const toValue = debouncedTo || undefined;

    const currentFrom = control.value?.from;
    const currentTo = control.value?.to;

    if (fromValue !== currentFrom || toValue !== currentTo) {
      if (!fromValue && !toValue) {
        control.onChange(undefined);
      } else {
        control.onChange({ from: fromValue, to: toValue });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires on debounced input only; adding control would re-emit the stale input after an external clear
  }, [debouncedFrom, debouncedTo]);

  // Sync local state when control.value changes externally (e.g., when clearing).
  const selectedFrom = control.value?.from;
  const selectedTo = control.value?.to;
  const [syncedTimeRange, setSyncedTimeRange] = useState({ from: selectedFrom, to: selectedTo });
  if (syncedTimeRange.from !== selectedFrom || syncedTimeRange.to !== selectedTo) {
    setSyncedTimeRange({ from: selectedFrom, to: selectedTo });
    setLocalFrom(selectedFrom ?? '');
    setLocalTo(selectedTo ?? '');
  }

  return (
    <div className='max-w-35'>
      {renderFilterTitle(control)}
      <div className='grid grid-cols-2 gap-1.5'>
        <NoddiTimeField label='From' value={localFrom} onChange={setLocalFrom} className='min-w-0' />
        <NoddiTimeField label='To' value={localTo} onChange={setLocalTo} className='min-w-0' />
      </div>
    </div>
  );
};
